import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import {
  FileText, Send, Instagram, Facebook, Linkedin, MapPin, Sparkles, Check,
  X, Edit3, ChevronDown, ChevronRight, Loader2, Star, MessageCircle, Copy,
  CheckCircle2, Circle, Flame, Info, ExternalLink, BarChart3, ClipboardCheck,
  Mic, Calendar, TrendingUp,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';
import PerformanceTab from '@/components/content/PerformanceTab';
import BrandVoiceTab from '@/components/content/BrandVoiceTab';
import CalendarTab from '@/components/content/CalendarTab';
import PublishTab from '@/components/content/PublishTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformConfig = {
  profile: { label: 'Blog', icon: FileText, color: '#1D6B4F' },
  instagram: { label: 'Instagram', icon: Instagram, color: '#E1306C' },
  facebook: { label: 'Facebook', icon: Facebook, color: '#1877F2' },
  linkedin: { label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  nextdoor: { label: 'Nextdoor', icon: MapPin, color: '#8ED500' },
  google: { label: 'Google', icon: Star, color: '#EA4335' },
};

const PLATFORM_GUIDES = {
  facebook: {
    icon: Facebook, color: '#1877F2', label: 'Facebook',
    steps: [
      'Open Facebook on your phone or go to facebook.com',
      'Go to your Business Page (not your personal profile)',
      'Tap "Create Post"',
      'Paste the text below (we\'ve copied it for you)',
      'Add a photo if you have one (photos get 2x more engagement)',
      'Tap "Post"',
    ],
    tip: 'Don\'t have a Facebook Business Page?',
    tipLink: '/guides/facebook',
  },
  instagram: {
    icon: Instagram, color: '#E1306C', label: 'Instagram',
    steps: [
      'Open the Instagram app',
      'Tap the + button at the bottom of the screen',
      'Choose a photo from your camera roll (a project photo, your team, or your workspace works great)',
      'Paste the caption below (we\'ve copied it for you)',
      'Tap "Share"',
    ],
    tip: 'Pro tip: Instagram posts with photos always perform better. Take a photo of your latest project!',
    tipLink: '/guides/instagram',
  },
  linkedin: {
    icon: Linkedin, color: '#0A66C2', label: 'LinkedIn',
    steps: [
      'Open LinkedIn on your phone or go to linkedin.com',
      'Tap "Start a post" at the top of your feed',
      'Paste the text below (we\'ve copied it for you)',
      'Tap "Post"',
    ],
    tip: 'LinkedIn posts help your professional credibility. AI uses LinkedIn profiles as trust signals.',
    tipLink: '/guides/linkedin',
  },
  google_business: {
    icon: MapPin, color: '#EA4335', label: 'Google Business Profile',
    steps: [
      'Open the Google Maps app or go to business.google.com',
      'Tap your business name',
      'Tap "Add update" or "Create post"',
      'Select "Add update"',
      'Paste the text below (we\'ve copied it for you)',
      'Add a photo and tap "Post"',
    ],
    tip: 'Don\'t have a Google Business Profile?',
    tipLink: '/guides/google-business',
  },
};

const filterTabs = [
  { key: 'all', label: 'All' },
  { key: 'blog', label: 'Blogs' },
  { key: 'social', label: 'Social' },
  { key: 'review_response', label: 'Reviews' },
  { key: 'requests', label: 'Requests' },
];

const WHY_HELPS_MAP = {
  blog: 'This article answers a question customers commonly ask AI. When published on your profile, AI can cite it as a source — putting YOUR name in the answer.',
  social: 'Social posts create "signals" that tell AI you\'re active and engaged. Each post strengthens your presence across platforms AI monitors.',
  review_response: 'Responding to reviews shows AI (and customers) that you\'re attentive and professional. This directly boosts your trust score.',
};

export default function Content() {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [contentStats, setContentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedCard, setExpandedCard] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [userTier, setUserTier] = useState('free');
  const [showEducation, setShowEducation] = useState(false);
  const [showPlatformGuide, setShowPlatformGuide] = useState(null);
  const [guideContentBody, setGuideContentBody] = useState('');
  const [activeTab, setActiveTab] = useState('content');

  const CONTENT_TABS = [
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'publish', label: 'Publish', icon: Send },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'voice', label: 'Brand Voice', icon: Mic },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
  ];

  useEffect(() => {
    const dismissed = localStorage.getItem('gcf_content_education_dismissed');
    if (!dismissed) setShowEducation(true);
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [contentRes, requestsRes, meRes, statsRes] = await Promise.all([
        axios.get(`${API}/content`, { withCredentials: true }),
        axios.get(`${API}/reviews/requests`, { withCredentials: true }).catch(() => ({ data: { requests: [], stats: {} } })),
        axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: { subscription_tier: 'free' } })),
        axios.get(`${API}/content/stats`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setContent(contentRes.data.content || []);
      setReviewRequests(requestsRes.data.requests || []);
      setReviewStats(requestsRes.data.stats || {});
      setUserTier(meRes.data.subscription_tier || 'free');
      setContentStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const generateContent = async () => {
    if (userTier === 'free') { navigate('/pricing'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/content/generate`, {}, { withCredentials: true });
      setContent(prev => [...(res.data.content || []), ...prev]);
    } catch (e) {
      if (e.response?.status === 403) navigate('/pricing');
    } finally {
      setGenerating(false);
    }
  };

  const handleAction = async (contentId, action, editedBody) => {
    try {
      const payload = { action };
      if (action === 'edit' && editedBody) payload.edited_body = editedBody;
      const res = await axios.put(`${API}/content/${contentId}`, payload, { withCredentials: true });
      setContent(prev => prev.map(c => c.content_id === contentId ? res.data : c));
      setEditingCard(null);
      setEditBody('');
    } catch (e) { /* action failed */ }
  };

  const handlePlatformPost = async (contentId, platform) => {
    try {
      const res = await axios.put(`${API}/content/${contentId}/platform/${platform}`, {}, { withCredentials: true });
      setContent(prev => prev.map(c => c.content_id === contentId ? res.data : c));
    } catch (e) { /* platform mark failed */ }
  };

  const dismissEducation = () => {
    localStorage.setItem('gcf_content_education_dismissed', 'true');
    setShowEducation(false);
  };

  const filtered = content.filter(c => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'blog') return c.type === 'blog';
    if (activeFilter === 'social') return c.type === 'social';
    if (activeFilter === 'review_response') return c.type === 'review_response';
    return true;
  });

  const drafts = filtered.filter(c => c.status === 'draft');
  const approved = filtered.filter(c => c.status === 'approved');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AppShell active="content">
    <div className="min-h-screen bg-[#F7F4EF] pb-24 desktop:pb-8" data-testid="content-page">
      {/* Education overlay */}
      <AnimatePresence>
        {showEducation && activeTab === 'content' && <EducationOverlay onDismiss={dismissEducation} />}
      </AnimatePresence>

      {/* Platform posting guide modal */}
      <AnimatePresence>
        {showPlatformGuide && (
          <PlatformGuideModal
            platform={showPlatformGuide}
            contentBody={guideContentBody}
            onClose={() => { setShowPlatformGuide(null); setGuideContentBody(''); }}
          />
        )}
      </AnimatePresence>

      <header className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between mb-1">
          <div>
            <p className="text-[11px] uppercase tracking-widest mb-1" style={{ color: '#B8985A' }}>Content Hub</p>
            <h1 className="font-display text-2xl font-medium text-[#3C3226]">Content</h1>
          </div>
          {activeTab === 'content' && (
            <button
              onClick={generateContent}
              disabled={generating}
              className="flex items-center gap-1.5 bg-[#3C3226] hover:opacity-85 disabled:opacity-60 text-[#F0EBE3] text-sm font-medium px-4 py-2.5 rounded-none transition-all active:scale-[0.97]"
              data-testid="generate-content-btn"
            >
              {generating ? <><Loader2 size={15} className="animate-spin" /> Generating...</> : <><Sparkles size={15} /> Generate</>}
            </button>
          )}
        </div>
        <p className="text-sm text-[#5C5347]">Create, track, and optimize your AI-ready content</p>
      </header>

      {/* Tab bar */}
      <div className="px-6 max-w-5xl mx-auto overflow-x-auto">
        <div className="flex items-center gap-1 mb-4 pb-px min-w-max" style={{ borderBottom: '1px solid #DDD5C8' }}>
          {CONTENT_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-3 py-2.5 text-[10px] uppercase tracking-wider transition-colors whitespace-nowrap"
              style={{
                color: activeTab === tab.id ? '#3C3226' : '#8C7E6E',
                fontWeight: activeTab === tab.id ? 600 : 400,
                borderBottom: activeTab === tab.id ? '2px solid #B8985A' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              data-testid={`content-tab-${tab.id}`}
            >
              <tab.icon size={12} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'publish' ? (
        <main className="px-6 max-w-5xl mx-auto"><PublishTab /></main>
      ) : activeTab === 'performance' ? (
        <main className="px-6 max-w-5xl mx-auto"><PerformanceTab /></main>
      ) : activeTab === 'voice' ? (
        <main className="px-6 max-w-5xl mx-auto"><BrandVoiceTab /></main>
      ) : activeTab === 'calendar' ? (
        <main className="px-6 max-w-5xl mx-auto"><CalendarTab /></main>
      ) : (
        <>
          {/* Posting streak + stats bar */}
          {contentStats && (contentStats.posting_streak_weeks > 0 || contentStats.total_drafts > 0) && (
            <div className="px-6 mb-3">
              <StreakBanner stats={contentStats} />
            </div>
          )}

          {/* Platform setup checklist (for users with no platform postings) */}
          {contentStats && contentStats.total_approved > 0 && (
            <PlatformSetupChecklist stats={contentStats} />
          )}

          {/* Nudge for unposted content */}
          {contentStats && contentStats.unposted_external > 0 && contentStats.total_approved > 2 && (
            <div className="px-6 mb-3">
              <div className="p-3.5 rounded-none bg-[#C67A5C]/8 border border-[#C67A5C]/15" data-testid="unposted-nudge">
                <p className="text-xs text-[#C67A5C] font-medium">
                  You approved {contentStats.unposted_external} piece{contentStats.unposted_external > 1 ? 's' : ''} of content but didn't share on social media yet. Sharing on Facebook and Google could boost your score by 5-8 points.
                </p>
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div className="px-6 mb-4">
            <div className="flex gap-2">
              {filterTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                    activeFilter === tab.key ? 'bg-[#3C3226] text-[#F0EBE3]' : 'bg-[#F7F4EF] text-[#5C5347] hover:bg-[#D4C9B8]'
                  }`}
                  data-testid={`filter-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <main className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
            {activeFilter === 'requests' ? (
              <ReviewRequestHistory requests={reviewRequests} stats={reviewStats} />
            ) : content.length === 0 && activeFilter === 'all' ? (
              <EmptyContentState onGenerate={generateContent} generating={generating} />
            ) : (
              <>
                {drafts.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#C67A5C] mb-3">
                      Needs your review ({drafts.length})
                    </h3>
                    <div className="space-y-3">
                      {drafts.map(item => (
                        <ContentCard
                          key={item.content_id}
                          item={item}
                          isExpanded={expandedCard === item.content_id}
                          isEditing={editingCard === item.content_id}
                          editBody={editBody}
                          onToggle={() => setExpandedCard(expandedCard === item.content_id ? null : item.content_id)}
                          onApprove={() => handleAction(item.content_id, 'approve')}
                          onReject={() => handleAction(item.content_id, 'reject')}
                          onStartEdit={() => { setEditingCard(item.content_id); setEditBody(item.body); }}
                          onSaveEdit={() => handleAction(item.content_id, 'edit', editBody)}
                          onCancelEdit={() => { setEditingCard(null); setEditBody(''); }}
                          onEditChange={setEditBody}
                          onOpenGuide={(platform, body) => { setShowPlatformGuide(platform); setGuideContentBody(body); }}
                          onPlatformPost={handlePlatformPost}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {approved.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#1D6B4F] mb-3">
                      Approved ({approved.length})
                    </h3>
                    <div className="space-y-3">
                      {approved.map(item => (
                        <ContentCard
                          key={item.content_id}
                          item={item}
                          isExpanded={expandedCard === item.content_id}
                          onToggle={() => setExpandedCard(expandedCard === item.content_id ? null : item.content_id)}
                          onOpenGuide={(platform, body) => { setShowPlatformGuide(platform); setGuideContentBody(body); }}
                          onPlatformPost={handlePlatformPost}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {filtered.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-sm text-[#5C5347]">No content matches this filter.</p>
                  </div>
                )}
              </>
            )}
          </main>
        </>
      )}
    </div>
    </AppShell>
  );
}

/* ── Education Overlay (Change 1.1) ── */
function EducationOverlay({ onDismiss }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-6"
      data-testid="education-overlay"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-[#F7F4EF] rounded-sm p-7 max-w-sm w-full shadow-xl"
      >
        <div className="w-14 h-14 rounded-none bg-[#1D6B4F]/10 flex items-center justify-center mb-5 mx-auto">
          <Sparkles size={28} className="text-[#1D6B4F]" />
        </div>
        <h2 className="text-lg font-bold text-[#3C3226] text-center mb-3">Why we create content for you</h2>
        <div className="space-y-3 text-sm text-[#5C5347] leading-relaxed">
          <p>When someone asks ChatGPT "best plumber near me," it mentions businesses it can find information about.</p>
          <p>Every blog post, social post, and review is a <span className="font-semibold text-[#3C3226]">SIGNAL</span> — it tells AI that you exist, you're active, and you're good at what you do.</p>
          <p className="font-semibold text-[#1D6B4F]">More signals = higher score = more customers.</p>
          <p>We write the content. You approve it. That's it.</p>
        </div>
        <button
          onClick={onDismiss}
          className="w-full mt-6 py-3 rounded-sm bg-[#1D6B4F] text-[#F0EBE3] font-semibold text-sm hover:bg-[#16563F] transition-colors flex items-center justify-center gap-2"
          data-testid="education-dismiss-btn"
        >
          Got it — show me my content <ChevronRight size={16} />
        </button>
      </motion.div>
    </motion.div>
  );
}

/* ── Posting Streak Banner (Change 4.2 + Milestones) ── */
function StreakBanner({ stats }) {
  const milestone = stats.milestone;

  if (milestone) {
    const tierColors = {
      gold: { bg: 'bg-[#EF9F27]/10', border: 'border-[#EF9F27]/20', icon: 'text-[#EF9F27]', text: 'text-[#EF9F27]' },
      silver: { bg: 'bg-[#888]/10', border: 'border-[#888]/20', icon: 'text-[#5C5347]', text: 'text-[#5C5347]' },
      bronze: { bg: 'bg-[#C67A5C]/10', border: 'border-[#C67A5C]/20', icon: 'text-[#C67A5C]', text: 'text-[#C67A5C]' },
    };
    const colors = tierColors[milestone.tier] || tierColors.bronze;

    return (
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`p-4 rounded-none ${colors.bg} border ${colors.border}`}
        data-testid="streak-milestone"
      >
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-sm ${colors.bg} flex items-center justify-center flex-shrink-0`}>
            <Star size={22} className={colors.icon} />
          </div>
          <div>
            <p className={`text-sm font-bold ${colors.text}`}>{milestone.label}</p>
            <p className="text-xs text-[#5C5347] mt-0.5">{milestone.message}</p>
            <p className="text-[10px] text-[#5C5347]/70 mt-1">{stats.posting_streak_weeks}-week streak active</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (stats.posting_streak_weeks > 0) {
    return (
      <div className="p-3.5 rounded-none bg-[#1D6B4F]/8 border border-[#1D6B4F]/15 flex items-center gap-3" data-testid="streak-banner">
        <div className="w-9 h-9 rounded-sm bg-[#1D6B4F]/15 flex items-center justify-center flex-shrink-0">
          <Flame size={18} className="text-[#1D6B4F]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#3C3226]">{stats.posting_streak_weeks}-week posting streak!</p>
          {stats.estimated_score_impact > 0 && (
            <p className="text-xs text-[#5C5347]">Your score went up ~{stats.estimated_score_impact} points from content.</p>
          )}
          {stats.posting_streak_weeks === 3 && (
            <p className="text-[10px] text-[#C67A5C] font-medium mt-0.5">1 more week for a milestone badge!</p>
          )}
        </div>
      </div>
    );
  }
  if (stats.total_drafts > 0) {
    return (
      <div className="p-3.5 rounded-none bg-[#EF9F27]/8 border border-[#EF9F27]/15 flex items-center gap-3" data-testid="streak-banner">
        <div className="w-9 h-9 rounded-sm bg-[#EF9F27]/15 flex items-center justify-center flex-shrink-0">
          <FileText size={18} className="text-[#EF9F27]" />
        </div>
        <p className="text-sm text-[#3C3226]">
          <span className="font-semibold">{stats.total_drafts} post{stats.total_drafts > 1 ? 's' : ''}</span> ready for this week. Approve and share to build your streak!
        </p>
      </div>
    );
  }
  return null;
}

/* ── Platform Setup Checklist (Change 4.3) ── */
function PlatformSetupChecklist({ stats }) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('gcf_platform_checklist_dismissed') === 'true');
  if (dismissed) return null;

  const platforms = [
    { key: 'getchosenfirst', label: 'GetChosenFirst profile', active: true },
    { key: 'google_business', label: 'Google Business Profile', link: '/guides/google-business', active: (stats.platform_counts?.google_business || 0) > 0 },
    { key: 'facebook', label: 'Facebook Business Page', link: '/guides/facebook', active: (stats.platform_counts?.facebook || 0) > 0 },
    { key: 'instagram', label: 'Instagram Business', link: '/guides/instagram', active: (stats.platform_counts?.instagram || 0) > 0 },
    { key: 'linkedin', label: 'LinkedIn Profile', link: '/guides/linkedin', active: (stats.platform_counts?.linkedin || 0) > 0 },
  ];

  const allActive = platforms.every(p => p.active);
  if (allActive) return null;

  return (
    <div className="px-6 mb-4">
      <div className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="platform-checklist">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={16} className="text-[#1D6B4F]" />
            <h3 className="text-sm font-semibold text-[#3C3226]">Platform setup</h3>
          </div>
          <button onClick={() => { localStorage.setItem('gcf_platform_checklist_dismissed', 'true'); setDismissed(true); }} className="text-xs text-[#5C5347] hover:text-[#3C3226]">Dismiss</button>
        </div>
        <p className="text-xs text-[#5C5347] mb-3">Each platform you activate increases the signals AI can find about your business.</p>
        <div className="space-y-2">
          {platforms.map(p => (
            <div key={p.key} className="flex items-center gap-2.5">
              {p.active ? (
                <CheckCircle2 size={16} className="text-[#1D6B4F] flex-shrink-0" />
              ) : (
                <Circle size={16} className="text-[#D4C9B8] flex-shrink-0" />
              )}
              <span className={`text-sm ${p.active ? 'text-[#1D6B4F]' : 'text-[#3C3226]'}`}>{p.label}</span>
              {!p.active && p.link && (
                <a href={p.link} className="text-xs text-[#1D6B4F] font-medium hover:underline ml-auto">Set up</a>
              )}
              {p.active && <span className="text-xs text-[#1D6B4F] ml-auto">Active</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Platform Guide Modal (Change 2.1) ── */
function PlatformGuideModal({ platform, contentBody, onClose }) {
  const guide = PLATFORM_GUIDES[platform];
  const [copied, setCopied] = useState(false);
  if (!guide) return null;
  const Icon = guide.icon;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(contentBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* clipboard failed */ }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="bg-[#F7F4EF] rounded-t-3xl sm:rounded-sm p-6 w-full max-w-md max-h-[85vh] overflow-y-auto"
        data-testid={`guide-modal-${platform}`}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${guide.color}15` }}>
            <Icon size={20} style={{ color: guide.color }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#3C3226]">Post to {guide.label}</h3>
            <p className="text-xs text-[#5C5347]">Step-by-step instructions</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1 rounded-none hover:bg-[#F7F4EF]"><X size={18} className="text-[#5C5347]" /></button>
        </div>

        <div className="space-y-3 mb-5">
          {guide.steps.map((step, idx) => (
            <div key={step.slice(0, 20)} className="flex gap-3">
              <div className="w-6 h-6 rounded-none bg-[#3C3226] text-[#F0EBE3] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{idx + 1}</div>
              <p className="text-sm text-[#3C3226] leading-relaxed">{step}</p>
            </div>
          ))}
        </div>

        {contentBody && (
          <button
            onClick={handleCopy}
            className={`w-full py-3 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
              copied ? 'bg-[#1D6B4F] text-[#F0EBE3]' : 'bg-[#3C3226] text-[#F0EBE3] hover:opacity-85'
            }`}
            data-testid="copy-content-btn"
          >
            {copied ? <><Check size={16} /> Copied! Now paste it into {guide.label}</> : <><Copy size={16} /> Copy text to clipboard</>}
          </button>
        )}

        {guide.tip && (
          <div className="mt-4 p-3 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
            <p className="text-xs text-[#5C5347]">
              {guide.tip.includes('?') ? (
                <>{guide.tip} <a href={guide.tipLink} className="text-[#1D6B4F] font-semibold hover:underline">Here's how</a></>
              ) : guide.tip}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Content Card (redesigned per Change 4.1) ── */
function ContentCard({
  item, isExpanded, isEditing, editBody,
  onToggle, onApprove, onReject, onStartEdit, onSaveEdit, onCancelEdit, onEditChange,
  onOpenGuide, onPlatformPost,
}) {
  const config = platformConfig[item.platform] || platformConfig.profile;
  const Icon = config.icon;
  const isDraft = item.status === 'draft';
  const isApproved = item.status === 'approved';
  const [showWhy, setShowWhy] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const swipeX = useMotionValue(0);
  const swipeBg = useTransform(swipeX, [-120, -40, 0, 40, 120], [
    'rgba(226,75,74,0.15)', 'rgba(226,75,74,0.05)', 'transparent', 'rgba(29,158,117,0.05)', 'rgba(29,158,117,0.15)'
  ]);
  const swipeOpacityLeft = useTransform(swipeX, [-120, -40, 0], [1, 0.3, 0]);
  const swipeOpacityRight = useTransform(swipeX, [0, 40, 120], [0, 0.3, 1]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 80 && onApprove) onApprove();
    else if (info.offset.x < -80 && onReject) onReject();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(item.body || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard failed */ }
  };

  const scoreImpact = item.type === 'blog' ? '+2-4 points' : item.type === 'social' ? '+1-2 points' : '+1 point';
  const platforms = item.platforms || {};
  const externalPlatforms = ['facebook', 'instagram', 'google_business', 'linkedin'];
  const postedCount = externalPlatforms.filter(p => platforms[p]?.posted || platforms[p]?.user_confirmed).length;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-none" data-testid={`content-card-${item.content_id}`}>
      {isDraft && (
        <motion.div className="absolute inset-0 rounded-none flex items-center justify-between px-5 z-0" style={{ backgroundColor: swipeBg }}>
          <motion.div style={{ opacity: swipeOpacityRight }} className="flex items-center gap-1 text-[#1D6B4F]"><Check size={20} strokeWidth={2} /><span className="text-xs font-semibold">Approve</span></motion.div>
          <motion.div style={{ opacity: swipeOpacityLeft }} className="flex items-center gap-1 text-[#E24B4A]"><span className="text-xs font-semibold">Skip</span><X size={20} strokeWidth={2} /></motion.div>
        </motion.div>
      )}

      <motion.div
        ref={cardRef}
        className="relative z-10 bg-[#F7F4EF] border border-[#D4C9B8] rounded-none overflow-hidden"
        drag={isDraft && !isEditing ? "x" : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.3}
        onDragEnd={handleDragEnd}
        style={{ x: isDraft ? swipeX : 0 }}
      >
        {/* Card header */}
        <button onClick={onToggle} className="w-full p-4 flex items-start gap-3 text-left" data-testid={`content-toggle-${item.content_id}`}>
          <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
            <Icon size={17} strokeWidth={1.5} style={{ color: config.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-medium uppercase tracking-wider" style={{ color: config.color }}>{config.label}</span>
              <StatusBadge status={item.status} />
              {/* Score impact indicator (Change 1.3) */}
              <span className="text-[10px] text-[#5C5347] ml-auto flex items-center gap-0.5">
                <BarChart3 size={10} /> {scoreImpact}
              </span>
              {item.schema_markup && (
                <span className="text-[9px] text-[#4285F4] bg-[#4285F4]/8 px-1.5 py-0.5 rounded font-medium" data-testid={`schema-badge-${item.content_id}`}>FAQ Ready</span>
              )}
            </div>
            <h4 className="text-sm font-medium text-[#3C3226] leading-snug">{item.title}</h4>
            {!isExpanded && <p className="text-xs text-[#5C5347] mt-1 line-clamp-2">{item.preview}</p>}
          </div>
          <ChevronDown size={16} className={`text-[#5C5347] flex-shrink-0 transition-transform mt-1 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Expanded content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="px-4 pb-4 border-t border-[#D4C9B8]">
                {isEditing ? (
                  <div className="mt-3">
                    <textarea value={editBody} onChange={e => onEditChange(e.target.value)} className="w-full p-3 rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] text-sm text-[#3C3226] leading-relaxed outline-none focus:ring-2 focus:ring-[#3C3226] resize-none" rows={8} data-testid={`edit-textarea-${item.content_id}`} />
                    <div className="flex gap-2 mt-2">
                      <button onClick={onSaveEdit} className="flex-1 py-2.5 rounded-sm bg-[#1D6B4F] text-[#F0EBE3] text-sm font-medium hover:bg-[#16563F]" data-testid={`save-edit-${item.content_id}`}>Save changes</button>
                      <button onClick={onCancelEdit} className="px-4 py-2.5 rounded-sm border border-[#D4C9B8] text-sm text-[#5C5347] hover:bg-[#F7F4EF]" data-testid={`cancel-edit-${item.content_id}`}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[#3C3226] mt-3 leading-relaxed whitespace-pre-wrap">{item.body}</p>

                    {/* "Why this helps" expandable (Change 1.2) */}
                    <button onClick={() => setShowWhy(!showWhy)} className="mt-3 flex items-center gap-1.5 text-xs text-[#1D6B4F] font-medium hover:text-[#3C3226] transition-colors" data-testid={`why-helps-toggle-${item.content_id}`}>
                      <Info size={13} />
                      {showWhy ? 'Hide' : 'Why this helps AI mention you'}
                      <ChevronDown size={12} className={`transition-transform ${showWhy ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {showWhy && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <div className="mt-2 p-3 rounded-sm bg-[#1D6B4F]/5 border border-[#1D6B4F]/10">
                            <p className="text-xs text-[#5C5347] leading-relaxed">{WHY_HELPS_MAP[item.type] || WHY_HELPS_MAP.social}</p>
                            <p className="text-xs text-[#5C5347] mt-2">Post to: <span className="font-semibold text-[#1D6B4F]">Your GetChosenFirst profile</span> (auto-published on approval)</p>
                            <p className="text-xs text-[#5C5347] mt-1">For extra impact, also share on:</p>
                            <div className="flex gap-2 mt-2">
                              {externalPlatforms.map(p => {
                                const g = PLATFORM_GUIDES[p];
                                if (!g) return null;
                                const PIcon = g.icon;
                                return (
                                  <button key={p} onClick={() => onOpenGuide && onOpenGuide(p, item.body)} className="flex items-center gap-1 px-2 py-1 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-xs text-[#5C5347] hover:border-[#1D6B4F] hover:text-[#1D6B4F] transition-colors" data-testid={`platform-btn-${p}`}>
                                    <PIcon size={12} style={{ color: g.color }} /> {g.label.split(' ')[0]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Copy to clipboard (Change 2.3) */}
                    <div className="flex gap-2 mt-3">
                      <button onClick={handleCopy} className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium transition-all ${copied ? 'bg-[#1D6B4F]/10 text-[#1D6B4F]' : 'bg-[#F7F4EF] text-[#5C5347] hover:bg-[#D4C9B8]'}`} data-testid={`copy-btn-${item.content_id}`}>
                        {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy text</>}
                      </button>
                    </div>

                    {/* Draft action buttons */}
                    {isDraft && (
                      <div className="flex gap-2 mt-3" data-testid={`actions-${item.content_id}`}>
                        <button onClick={onApprove} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm bg-[#1D6B4F]/10 text-[#1D6B4F] text-sm font-medium hover:bg-[#1D6B4F]/20" data-testid={`approve-${item.content_id}`}><Check size={15} strokeWidth={2} /> Approve</button>
                        <button onClick={onStartEdit} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm bg-[#EF9F27]/10 text-[#EF9F27] text-sm font-medium hover:bg-[#EF9F27]/20" data-testid={`edit-${item.content_id}`}><Edit3 size={15} strokeWidth={2} /> Edit</button>
                        <button onClick={onReject} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-sm bg-[#E24B4A]/10 text-[#E24B4A] text-sm font-medium hover:bg-[#E24B4A]/20" data-testid={`reject-${item.content_id}`}><X size={15} strokeWidth={2} /> Skip</button>
                      </div>
                    )}

                    {/* Platform posting status (Change 3.2) — shown for approved content */}
                    {isApproved && (
                      <div className="mt-3 p-3 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid={`platform-status-${item.content_id}`}>
                        <p className="text-xs font-semibold text-[#3C3226] mb-2">Publishing status ({postedCount + (platforms.getchosenfirst?.posted ? 1 : 0)}/5)</p>
                        <div className="space-y-2">
                          {/* GetChosenFirst auto-published */}
                          <div className="flex items-center gap-2">
                            {platforms.getchosenfirst?.posted ? <CheckCircle2 size={14} className="text-[#1D6B4F]" /> : <Circle size={14} className="text-[#D4C9B8]" />}
                            <span className="text-xs text-[#5C5347]">GetChosenFirst profile</span>
                            {platforms.getchosenfirst?.auto && <span className="text-[10px] text-[#1D6B4F] font-medium ml-auto">(auto)</span>}
                          </div>
                          {/* External platforms */}
                          {externalPlatforms.map(p => {
                            const g = PLATFORM_GUIDES[p];
                            if (!g) return null;
                            const isPosted = platforms[p]?.posted || platforms[p]?.user_confirmed;
                            return (
                              <div key={p} className="flex items-center gap-2">
                                {isPosted ? <CheckCircle2 size={14} className="text-[#1D6B4F]" /> : <Circle size={14} className="text-[#D4C9B8]" />}
                                <span className="text-xs text-[#5C5347]">{g.label}</span>
                                {!isPosted && (
                                  <button
                                    onClick={() => { onOpenGuide && onOpenGuide(p, item.body); }}
                                    className="text-[10px] text-[#1D6B4F] font-medium ml-auto hover:underline flex items-center gap-0.5"
                                    data-testid={`guide-btn-${p}-${item.content_id}`}
                                  >
                                    How to post <ExternalLink size={9} />
                                  </button>
                                )}
                                {!isPosted && (
                                  <button
                                    onClick={() => onPlatformPost && onPlatformPost(item.content_id, p)}
                                    className="text-[10px] text-[#5C5347] hover:text-[#1D6B4F] ml-1"
                                    data-testid={`mark-posted-${p}-${item.content_id}`}
                                  >
                                    Mark done
                                  </button>
                                )}
                                {isPosted && <span className="text-[10px] text-[#1D6B4F] ml-auto">Done</span>}
                              </div>
                            );
                          })}
                        </div>
                        {postedCount < 4 && (
                          <p className="text-[10px] text-[#C67A5C] mt-2 font-medium">
                            {4 - postedCount} more platform{4 - postedCount > 1 ? 's' : ''} to go for maximum impact!
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {isDraft && !isExpanded && (
          <div className="px-4 pb-2.5 flex items-center justify-center gap-3 sm:hidden">
            <span className="text-[10px] text-[#5C5347]/60 tracking-wide">swipe right to approve  ·  left to skip</span>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    draft: 'bg-[#EF9F27]/10 text-[#EF9F27]',
    approved: 'bg-[#1D6B4F]/10 text-[#1D6B4F]',
    rejected: 'bg-[#E24B4A]/10 text-[#E24B4A]',
    published: 'bg-[#1D6B4F]/10 text-[#1D6B4F]',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${styles[status] || styles.draft}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

function EmptyContentState({ onGenerate, generating }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center py-16" data-testid="empty-content-state">
      <div className="w-20 h-20 rounded-none bg-[#3C3226]/5 flex items-center justify-center mb-6">
        <Sparkles size={32} strokeWidth={1} className="text-[#1D6B4F]" />
      </div>
      <h2 className="font-display text-xl font-medium text-[#3C3226] mb-2">Your content engine</h2>
      <p className="text-sm text-[#5C5347] leading-relaxed max-w-xs mb-8">
        We'll generate blog posts, social media content, and review responses tailored to your business. Approve what you like, skip what you don't.
      </p>
      <button onClick={onGenerate} disabled={generating} className="flex items-center gap-2 bg-[#2C2520] hover:bg-[#3C3226] disabled:opacity-60 text-[#F0EBE3] px-8 py-4 text-lg font-medium transition-all active:scale-[0.97]" data-testid="generate-first-content-btn">
        {generating ? <><Loader2 size={20} className="animate-spin" /> Generating content...</> : <><Sparkles size={20} /> Generate my first batch</>}
      </button>
    </motion.div>
  );
}

function ReviewRequestHistory({ requests, stats }) {
  const statusColors = {
    waiting: { bg: 'bg-[#EF9F27]/10', text: 'text-[#EF9F27]', label: 'Waiting' },
    posted: { bg: 'bg-[#1D6B4F]/10', text: 'text-[#1D6B4F]', label: 'Posted' },
    expired: { bg: 'bg-[#888]/10', text: 'text-[#5C5347]', label: 'Expired' },
  };

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-16" data-testid="empty-requests">
        <div className="w-16 h-16 rounded-none bg-[#C67A5C]/5 flex items-center justify-center mb-4"><Send size={28} strokeWidth={1} className="text-[#C67A5C]" /></div>
        <h3 className="font-display text-lg font-medium text-[#3C3226] mb-2">No review requests yet</h3>
        <p className="text-sm text-[#5C5347] max-w-xs">Tap "Ask for a review" on the Home tab to send your first text to a customer.</p>
      </div>
    );
  }

  return (
    <div data-testid="review-request-history">
      <div className="flex gap-3 mb-4">
        <div className="flex-1 p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-center">
          <span className="text-lg font-bold text-[#3C3226]">{stats.total || 0}</span>
          <p className="text-[10px] text-[#5C5347] mt-0.5">Sent</p>
        </div>
        <div className="flex-1 p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-center">
          <span className="text-lg font-bold text-[#EF9F27]">{stats.waiting || 0}</span>
          <p className="text-[10px] text-[#5C5347] mt-0.5">Waiting</p>
        </div>
        <div className="flex-1 p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-center">
          <span className="text-lg font-bold text-[#1D6B4F]">{stats.posted || 0}</span>
          <p className="text-[10px] text-[#5C5347] mt-0.5">Posted</p>
        </div>
      </div>
      <div className="space-y-2">
        {requests.map((req) => {
          const status = statusColors[req.review_status] || statusColors.waiting;
          return (
            <motion.div key={req.request_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] flex items-center gap-3" data-testid={`review-request-${req.request_id}`}>
              <div className="w-9 h-9 rounded-sm bg-[#C67A5C]/10 flex items-center justify-center flex-shrink-0"><Send size={15} strokeWidth={1.5} className="text-[#C67A5C]" /></div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-[#3C3226] block">{req.customer_name}</span>
                <span className="text-xs text-[#5C5347]">{new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>{status.label}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
